import classNames from 'classnames'
import { useMemo, useState, useCallback, useRef, useEffect, memo } from 'react';
import useLocalStorageState from 'use-local-storage-state'
import Flag from 'react-flagkit'
import { useReactTable, flexRender, getCoreRowModel } from '@tanstack/react-table'
import { useQuery, useQueryClient, useMutation, useIsMutating } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { deleteSingleAsin } from '../redux/slices/priceAlertInfoSlice'
import { getStoreSearchAPI, getWholesaleProductsAPI, getB2BSupplierProductsAPI, toggleActionButtonAPI, updateWholesaleEntryAPI } from '../lib/api/api'
import { toast } from 'react-toastify'
import { useUpdateFilterMutation, useFiltersQuery, useFilterStore } from '../services/filters'
import Tippy from '@tippyjs/react'
import AICheckButton from './AICheckButton'
import CommonModal from './CommonModal'
import ProductFavorite from './ProductFavorite'
import FavoriteStar from './FavoriteStar'
import MismatchIcon from './MismatchIcon'
import Button from './Button'
import ProductCardView from './ProductCardView'

import styles from './ProductGrid.module.css'
import 'tippy.js/dist/tippy.css'


import { ReactComponent as ExportIcon } from '../icons/export.svg'
import { ReactComponent as SortIcon } from '../icons/arrow.svg'
import { ReactComponent as ExchangeIcon } from '../icons/exchange.svg'
import { ReactComponent as A1Icon } from '../icons/a1.svg'
import { ReactComponent as SasIcon } from '../icons/sas.svg'
import { ReactComponent as AmazonIcon } from '../icons/amazon.svg'

import { ReactComponent as TrashIcon } from '../icons/trash.svg'
import Pill from './Pill'
import AIAnalysisTag from './AIAnalysisTag'
import { currencyFormatter, formatBerlinPingTimestamp } from '../utlis/utlis'
import { resolveRegionAIAnalysis } from '../utlis/aiAnalysis'
import { buildSupplierProductUrl } from '../lib/supplierLinks'
import { useTheme } from '../context/themeContext'
import PaginationBar from './PaginationBar'
import { RIGHT_PINNED_COLUMNS, SORT_COLUMNS, THIRTY_DAY_SORT_MAP, WHOLESALE_ONLY_SORT_COLUMNS, normalizeRegions, deriveRegionsFromData, arraysEqual } from './ProductGrid.helpers'
import { SeasonalityCell, VelocityPriceCell, computeVelocityPriceRange, createSeasonalityDatasets, resolveVelocityPrice } from './ProductGridSeasonality'
import useColumnSettingsSync, { useColumnSettingsLoader } from '../hooks/useColumnSettingsSync'
import { scrollToTopFrom } from './scrollToTop'

let Center = ({ children }) => <div className='flex items-center justify-center'>{children}</div>

const ALLOWED_PAGE_SIZES = [20, 50, 100]

const normalizeRegionKey = (key) => typeof key === 'string' ? key.toLowerCase() : key

// Skeleton Loading Components
function SkeletonCell({ columnId, regions }) {
  return (
    <div className="animate-pulse">
      {columnId === 'supplier_img' || columnId === 'amz_img' ? (
        <div className="w-28 h-28 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
      ) : columnId === 'keepa_chart_dk' ? (
        <div className="w-72 h-28 bg-gray-200 dark:bg-gray-700 rounded"></div>
      ) : columnId === 'flags' ? (
        <div className="flex flex-col gap-1">
          {Array.from({ length: regions.length }).map((_, i) => (
            <div key={i} className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
          ))}
        </div>
      ) : columnId === 'actions' ? (
        <div className="grid grid-cols-2 gap-1 w-fit mx-auto">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-1 items-center">
          {Array.from({ length: regions.length || 1 }).map((_, i) => (
            <div key={i} className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
          ))}
        </div>
      )}
    </div>
  )
}

function SkeletonRow({ columns, regions, index = 0 }) {
  return (
    <tr className={classNames(index > 0 && 'border-t', 'border-gray-200 dark:border-gray-700')}>
      {columns.map((column, index) => (
        <td key={index} className="p-2">
          <SkeletonCell columnId={column.id} regions={regions} />
        </td>
      ))}
    </tr>
  )
}

function SkeletonTable({ table, columns, regions, pagination, totalPages = 0, hasNext, setPagination, outerScroll = false, onPageViewportReset }) {
  const handlePageChange = (page) => {
    onPageViewportReset?.()
    setPagination({ ...pagination, pageIndex: page })
  }

  const handlePageSizeChange = (size) => {
    onPageViewportReset?.()
    setPagination({ pageIndex: 0, pageSize: size })
  }

  return (
    <>
      <div className={`relative border-t border-gray-200 dark:border-gray-700 ${outerScroll ? 'min-w-max' : 'overflow-x-auto shrink-0'}`} style={{ overflowAnchor: 'none' }}>
        <table className={`${styles.productGrid} min-w-full font-medium border-spacing-2 dark:text-white border-collapse w-max`}>
          <thead className='sticky top-0 z-10 dark:text-white'>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, at) => (
                  <th key={header.id} className='text-center'>
                    <div className='inline-flex items-center justify-center px-3 rounded-full bg-neutral-100 dark:bg-neutral-800'>
                      {header.column.columnDef.header && flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </div>
                  </th>
                ))}
                <th className='min-w-20 w-full'></th>
              </tr>
            ))}
          </thead>
          <tbody>
            {Array.from({ length: pagination.pageSize }).map((_, index) => (
              <SkeletonRow key={`skeleton-${index}`} columns={columns} regions={regions} index={index} />
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar
        pageIndex={pagination.pageIndex}
        totalPages={totalPages}
        hasNext={hasNext}
        setPage={handlePageChange}
        pageSize={pagination.pageSize}
        setPageSize={handlePageSizeChange}
      />
    </>
  );
}

export function SkeletonPill() {
  return (
    <span className='inline-block w-12 h-4 bg-gray-300 dark:bg-gray-700 rounded-full animate-pulse' />
  )
}

export function EditableBuypriceCell({ value, editable, isPending, onSubmit }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const startEdit = useCallback(() => {
    if (!editable || isPending) return
    const num = Number(value)
    const current = Number.isFinite(num) ? num.toFixed(2) : ''
    setDraft(current)
    setEditing(true)
  }, [editable, isPending, value])

  const cancel = useCallback(() => {
    setEditing(false)
    setDraft('')
  }, [])

  const commit = useCallback(() => {
    const trimmed = (draft ?? '').toString().trim().replace(',', '.')
    if (trimmed === '') {
      cancel()
      return
    }
    const parsed = Number(trimmed)
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 99999.99) {
      toast.error('Buyprice muss zwischen 0 und 99999.99 liegen.')
      cancel()
      return
    }
    const rounded = Math.round(parsed * 100) / 100
    const currentNum = Number(value)
    if (Number.isFinite(currentNum) && Math.abs(currentNum - rounded) < 1e-9) {
      cancel()
      return
    }
    setEditing(false)
    setDraft('')
    onSubmit(rounded)
  }, [draft, value, onSubmit, cancel])

  if (isPending) {
    return (
      <div className='h-8 flex items-center w-full justify-center'>
        <SkeletonPill />
      </div>
    )
  }

  if (editing) {
    // Maske: bis zu 5 Ziffern vor Komma/Punkt, optional Separator, bis 2 Nachkommastellen.
    // Komma/Punkt werden akzeptiert; Vereinheitlichung auf '.' passiert beim commit().
    const sanitize = (raw) => {
      if (typeof raw !== 'string') return ''
      let s = raw.replace(/[^0-9.,]/g, '')  // alles ausser digits + separator weg
      const firstSep = s.search(/[.,]/)
      if (firstSep !== -1) {
        const before = s.slice(0, firstSep).replace(/[.,]/g, '')
        const sep = s[firstSep]
        const after = s.slice(firstSep + 1).replace(/[.,]/g, '')
        s = before + sep + after
      }
      const m = s.match(/^(\d{0,5})([.,]?)(\d{0,2}).*$/)
      if (!m) return ''
      return m[1] + m[2] + m[3]
    }
    return (
      <div className='h-8 flex items-center w-full justify-center'>
        <input
          ref={inputRef}
          type='text'
          inputMode='decimal'
          autoComplete='off'
          spellCheck={false}
          maxLength={9}
          value={draft}
          onChange={(e) => setDraft(sanitize(e.target.value))}
          onPaste={(e) => {
            e.preventDefault()
            const pasted = (e.clipboardData || window.clipboardData).getData('text')
            setDraft(sanitize(pasted))
          }}
          onBeforeInput={(e) => {
            const ch = e.data
            if (ch && /[^0-9.,]/.test(ch)) {
              e.preventDefault()
            }
          }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commit()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              cancel()
            }
          }}
          className='w-20 h-6 px-2 text-center rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-900 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-primary'
        />
      </div>
    )
  }

  const displayNum = Number(value)
  const display = Number.isFinite(displayNum)
    ? new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(displayNum)
    : currencyFormatter(value)

  return (
    <div
      className={classNames('h-8 flex items-center w-full justify-center', editable && 'cursor-pointer')}
      onDoubleClick={editable ? startEdit : undefined}
      title={editable ? 'Doppelklick zum Bearbeiten' : undefined}
    >
      <Pill>
        {display}€
      </Pill>
    </div>
  )
}

export function EditablePackageQuantityCell({ value, userValue, editable, isPending, onSubmit }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef(null)
  const hasUserValue = userValue !== null && userValue !== undefined
  const displayValue = value ?? 1

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const startEdit = useCallback(() => {
    if (!editable || isPending) return
    setDraft(String(displayValue))
    setEditing(true)
  }, [editable, isPending, displayValue])

  const cancel = useCallback(() => {
    setEditing(false)
    setDraft('')
  }, [])

  const commit = useCallback(() => {
    const trimmed = (draft ?? '').toString().trim()
    // Leer + User-Wert vorhanden -> Reset (Backend loescht den User-Wert, Amazon-Default greift wieder)
    if (trimmed === '') {
      if (hasUserValue) {
        setEditing(false)
        setDraft('')
        onSubmit(null)
      } else {
        cancel()
      }
      return
    }
    const parsed = Number(trimmed)
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 999) {
      toast.error('Einheiten muss eine ganze Zahl zwischen 1 und 999 sein.')
      cancel()
      return
    }
    // No-op wenn unveraendert (nur wenn User-Wert vorhanden war -- sonst nie "unveraendert")
    if (hasUserValue && parsed === userValue) {
      cancel()
      return
    }
    setEditing(false)
    setDraft('')
    onSubmit(parsed)
  }, [draft, hasUserValue, userValue, onSubmit, cancel])

  const sanitize = (raw) => {
    if (typeof raw !== 'string') return ''
    return raw.replace(/[^0-9]/g, '').slice(0, 3)
  }

  if (isPending) {
    return (
      <div className='h-8 flex items-center w-full justify-center'>
        <SkeletonPill />
      </div>
    )
  }

  if (editing) {
    return (
      <div className='h-8 flex items-center w-full justify-center'>
        <input
          ref={inputRef}
          type='text'
          inputMode='numeric'
          autoComplete='off'
          spellCheck={false}
          maxLength={3}
          placeholder={hasUserValue ? 'leer = reset' : undefined}
          value={draft}
          onChange={(e) => setDraft(sanitize(e.target.value))}
          onPaste={(e) => {
            e.preventDefault()
            const pasted = (e.clipboardData || window.clipboardData).getData('text')
            setDraft(sanitize(pasted))
          }}
          onBeforeInput={(e) => {
            if (e.data && /[^0-9]/.test(e.data)) e.preventDefault()
          }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit() }
            else if (e.key === 'Escape') { e.preventDefault(); cancel() }
          }}
          className='w-16 h-6 px-2 text-center rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-900 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-primary'
        />
      </div>
    )
  }

  return (
    <div
      className={classNames('h-8 flex items-center w-full justify-center', editable && 'cursor-pointer')}
      onDoubleClick={editable ? startEdit : undefined}
      title={editable ? 'Doppelklick zum Bearbeiten' : undefined}
    >
      <Pill>{`×${displayValue}`}</Pill>
    </div>
  )
}

export default function ProductGrid({
  application,
  regions: controlledRegions,
  search,
  hiddenColumnIds = [],
  setHiddenColumnIds,
  listId,
  favoriteId,
  supplierPrefix,
  buyRegions,
  storageKeyPrefix,
  columnSettingsModule,
  outerScroll = false,
  onPageViewportReset,
}) {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const controlledRegionsNormalized = useMemo(() => {
    if (controlledRegions === undefined || controlledRegions === null) {
      return null
    }
    return normalizeRegions(controlledRegions)
  }, [controlledRegions])

  const [internalRegions, setInternalRegions] = useState(() => controlledRegionsNormalized ?? [])

  useEffect(() => {
    if (controlledRegionsNormalized === null) {
      return
    }

    if (!arraysEqual(internalRegions, controlledRegionsNormalized)) {
      setInternalRegions(controlledRegionsNormalized)
    }
  }, [controlledRegionsNormalized, internalRegions])

  const regions = useMemo(() => {
    if (controlledRegionsNormalized !== null) {
      return controlledRegionsNormalized
    }
    return internalRegions
  }, [controlledRegionsNormalized, internalRegions])

  const isWholesaleMode = application === 'wholesale_wizard' && (listId || favoriteId || supplierPrefix)
  
  // Gemeinsamer 30d-Umschalter für Margin, ROI, Profit, Sellprice
  let [use30d, setUse30d] = useState(false)
  let [hiddenAsins, setHiddenAsins] = useState([])
  let [favoriteModalProduct, setFavoriteModalProduct] = useState(null)
  let [favoritePendingAsins, setFavoritePendingAsins] = useState({})
  let [mismatchPendingAsins, setMismatchPendingAsins] = useState({})

  const handleFavoriteModalOpen = useCallback((product) => {
    setFavoriteModalProduct(product)
  }, [])

  const handleFavoriteModalClose = useCallback(() => {
    setFavoriteModalProduct(null)
  }, [])

  const resolveAsin = useCallback((product) => {
    const rawAsin = product?.asin
    return Array.isArray(rawAsin) ? rawAsin[0] : rawAsin
  }, [])

  const resolvePingId = useCallback((product, fallbackAsin) => {
    return product?.ping_id || product?.pingId || fallbackAsin
  }, [])

  const resolveMismatchActive = useCallback((value) => {
    return value === true || value === 1 || value === '1' || value === 'true'
  }, [])
  
  const resolvePregen = useCallback((value) => {
    return value === true || value === 1 || value === '1' || value === 'true'
  }, [])

  const buildDealsDirektLink = useCallback((asin, region, buyprice = '', cost = '') => {
    if (!asin) return undefined
    const safeRegion = region || 'de'
    let url = `https://dealsdirekt.de/go.html?asin=${encodeURIComponent(asin)}&region=${encodeURIComponent(safeRegion)}`
    if (buyprice) {
      url += `&bbp-buyprice=${encodeURIComponent(buyprice)}`
    }
    const safeCost = cost || buyprice
    if (safeCost) {
      url += `&sas_cost_price=${encodeURIComponent(safeCost)}`
    }
    return url
  }, [])

  // Stabiler Chart Column (ändert sich nur bei Regions)
  const chartColumn = useMemo(() => ({
    id: 'keepa_chart_dk',
    header: 'Chart',
    accessorKey: 'keepa_cahrts',
    enableResizing: false,
    className: styles.chartCell,
    cell: ({ getValue, row }) => (
      <MemoChart
        charts={getValue()}
        regions={regions}
        key={row.id}
      />
    ),
  }), [regions])

  let [sort, setSort] = useState({ sortBy: undefined, sortType: undefined })
  const [storedPageSize, setStoredPageSize] = useLocalStorageState(`${storageKeyPrefix ?? application}-page-size`, {
    defaultValue: 20
  })
  const [pageIndex, setPageIndex] = useState(0)
  const pagination = useMemo(() => ({
    pageIndex,
    pageSize: ALLOWED_PAGE_SIZES.includes(storedPageSize) ? storedPageSize : 20,
  }), [pageIndex, storedPageSize])
  const setPagination = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(pagination) : updater
    const safeSize = ALLOWED_PAGE_SIZES.includes(next.pageSize) ? next.pageSize : 20
    const safePage = Number.isInteger(next.pageIndex) && next.pageIndex >= 0 ? next.pageIndex : 0
    if (safeSize !== storedPageSize) {
      setStoredPageSize(safeSize)
    }
    setPageIndex(safePage)
  }, [pagination, storedPageSize, setStoredPageSize])

  // Solange nach einem Kontext-Reset (Filter/Suche/Sort/Region) noch keine frische
  // Antwort da ist, zeigt placeholderData die alten Daten -> alte Gesamtseitenzahl im
  // Footer. Flag klemmt die Totals bis zum Eintreffen der Antwort auf die Reset-Seite.
  const [totalsResetPending, setTotalsResetPending] = useState(false)

  // Laufende Filter-Writes (Pills/Modal/Presets) beobachten. Solange welche laufen,
  // wird die Daten-Query pausiert: sonst feuert der pageIndex-Reset (Key-Wechsel)
  // einen Fetch, bevor der Server die neuen Filter bestaetigt hat -> kurz falsch
  // gefilterte Produkte. Erst wenn der letzte Write durch ist, holt genau EIN Fetch
  // den bestaetigten Stand. Skeleton zeigt nur bei 'data'-Writes (Pills) -- Modal-
  // Edits schieben den Reload bewusst bis zum Schliessen auf.
  const pendingFilterWrites = useIsMutating({ mutationKey: ['filter-write', application] })
  const pendingDataFilterWrites = useIsMutating({ mutationKey: ['filter-write', application, 'data'] })

  // Offenes Filter-Panel = Hold: Daten-Query bleibt pausiert und der pageIndex-
  // Reset wird aufgeschoben, bis das Panel schliesst. Sonst laedt die Produkt-
  // liste live um, waehrend der User noch Filter zusammenstellt (Key-Wechsel
  // durch den Reset bzw. Stale-Refetch beim Re-Enable nach jedem Write).
  const filterPanelOpen = useFilterStore((s) => Boolean(s.openFilterPanels?.[application]))
  const [pendingFilterPageReset, setPendingFilterPageReset] = useState(false)

  // Filter liegen serverseitig; Aenderungen (Filters-Komponente, Presets, Blacklist)
  // invalidieren nur ['filters'] + ['data'] und erreichen diese Komponente sonst nicht.
  // Den Filter-Cache beobachten und bei echter Aenderung auf Seite 1 zurueckspringen --
  // sonst zeigt der alte pageIndex ins neue (kleinere) Ergebnis und die Seite ist leer.
  const { data: filtersData } = useFiltersQuery(application)
  const filtersSignature = filtersData === undefined
    ? undefined
    : JSON.stringify(filtersData?.current_filter ?? null)
  const lastFiltersSignatureRef = useRef()
  useEffect(() => {
    if (filtersSignature === undefined) return
    const prev = lastFiltersSignatureRef.current
    lastFiltersSignatureRef.current = filtersSignature
    if (prev !== undefined && prev !== filtersSignature) {
      if (filterPanelOpen) {
        setPendingFilterPageReset(true)
        return
      }
      setPageIndex(0)
      setTotalsResetPending(true)
    }
  }, [filtersSignature, filterPanelOpen])

  // Panel zu -> aufgeschobenen Reset anwenden. Die Query ist bis dahin ueber
  // pendingFilterPageReset weiter disabled, damit nicht erst der alte Key
  // (alte Seite) fetcht und direkt danach der Reset-Key (Doppel-Request).
  useEffect(() => {
    if (!filterPanelOpen && pendingFilterPageReset) {
      setPendingFilterPageReset(false)
      setPageIndex(0)
      setTotalsResetPending(true)
    }
  }, [filterPanelOpen, pendingFilterPageReset])

  // Suche/Sortierung/Regionen/Listen-Wechsel -> ebenfalls zurueck auf Seite 1.
  // Nur die von aussen gesteuerten Regionen als Key, nicht die aus Daten abgeleiteten
  // (die koennen sich beim Blaettern aendern und wuerden sonst zurueckspringen).
  const controlledRegionsResetKey = controlledRegionsNormalized ? controlledRegionsNormalized.join(',') : ''
  useEffect(() => {
    setPageIndex(0)
    setTotalsResetPending(true)
  }, [application, listId, favoriteId, supplierPrefix, search, sort.sortBy, sort.sortType, controlledRegionsResetKey])

  // Mutation für das Hinzufügen eines Suppliers zur Blacklist (über Filter-System)
  const addSupplierToBlacklistMutation = useUpdateFilterMutation(application, true)
  const dataQueryKey = useMemo(() => {
    if (isWholesaleMode) {
      return [
        'data',
        application,
        listId,
        favoriteId,
        supplierPrefix,
        search,
        sort.sortBy,
        sort.sortType,
        pagination.pageIndex,
        pagination.pageSize,
        regions,
      ]
    }

    return [
      'data',
      application,
      regions,
      search,
      sort.sortBy,
      sort.sortType,
      pagination.pageIndex,
      pagination.pageSize,
    ]
  }, [application, favoriteId, isWholesaleMode, listId, supplierPrefix, pagination.pageIndex, pagination.pageSize, regions, search, sort.sortBy, sort.sortType])

  const updateFavoriteInCache = useCallback((asin, favorite) => {
    queryClient.setQueryData(dataQueryKey, (prev) => {
      if (!prev?.products) return prev
      return {
        ...prev,
        products: prev.products.map((product) => {
          const productAsin = Array.isArray(product?.asin) ? product.asin[0] : product?.asin
          if (String(productAsin) !== String(asin)) return product
          return {
            ...product,
            favorite,
          }
        }),
      }
    })
  }, [dataQueryKey, queryClient])

  const updateMismatchInCache = useCallback((asin, mismatch) => {
    queryClient.setQueryData(dataQueryKey, (prev) => {
      if (!prev?.products) return prev
      return {
        ...prev,
        products: prev.products.map((product) => {
          const productAsin = Array.isArray(product?.asin) ? product.asin[0] : product?.asin
          if (String(productAsin) !== String(asin)) return product
          return {
            ...product,
            mismatch,
          }
        }),
      }
    })
  }, [dataQueryKey, queryClient])

  const toggleFavoriteMutation = useMutation({
    mutationFn: ({ action, asin, pingId }) =>
      toggleActionButtonAPI(action, application, 'favorite', asin, pingId),
    onMutate: ({ asin }) => {
      setFavoritePendingAsins((prev) => {
        const next = { ...prev }
        next[asin] = true
        return next
      })
    },
    onSuccess: (data, variables) => {
      if (data?.status !== 'authorized') {
        return
      }
      updateFavoriteInCache(variables.asin, variables.action === 'add')
    },
    onError: () => {
      toast.error('Favorit konnte nicht aktualisiert werden.')
    },
    onSettled: (_data, _error, variables) => {
      setFavoritePendingAsins((prev) => {
        const next = { ...prev }
        delete next[variables.asin]
        return next
      })
    },
  })

  const toggleMismatchMutation = useMutation({
    mutationFn: ({ action, asin, pingId }) =>
      toggleActionButtonAPI(action, application, 'mismatch', asin, pingId),
    onMutate: ({ asin }) => {
      setMismatchPendingAsins((prev) => {
        const next = { ...prev }
        next[asin] = true
        return next
      })
    },
    onSuccess: (data, variables) => {
      if (data?.status !== 'authorized') {
        return
      }
      updateMismatchInCache(variables.asin, variables.action === 'add')
    },
    onError: () => {
      toast.error('Mismatch konnte nicht aktualisiert werden.')
    },
    onSettled: (_data, _error, variables) => {
      setMismatchPendingAsins((prev) => {
        const next = { ...prev }
        delete next[variables.asin]
        return next
      })
    },
  })

  const handleFavoriteToggle = useCallback((product) => {
    const asin = resolveAsin(product)
    if (!asin) {
      toast.error('Kein ASIN gefunden.')
      return
    }
    const pingId = resolvePingId(product, asin)
    const action = product?.favorite ? 'remove' : 'add'
    toggleFavoriteMutation.mutate({ action, asin, pingId })
  }, [resolveAsin, resolvePingId, toggleFavoriteMutation])

  const handleMismatchToggle = useCallback((product) => {
    const asin = resolveAsin(product)
    if (!asin) {
      toast.error('Kein ASIN gefunden.')
      return
    }
    const pingId = resolvePingId(product, asin)
    const action = resolveMismatchActive(product?.mismatch) ? 'remove' : 'add'
    toggleMismatchMutation.mutate({ action, asin, pingId })
  }, [resolveAsin, resolveMismatchActive, resolvePingId, toggleMismatchMutation])

  let [pendingRowIds, setPendingRowIds] = useState(() => new Set())

  const updateWholesaleEntryMutation = useMutation({
    mutationFn: updateWholesaleEntryAPI,
    onMutate: ({ publicId }) => {
      setPendingRowIds((prev) => {
        const next = new Set(prev)
        next.add(publicId)
        return next
      })
    },
    onSuccess: (response, variables) => {
      if (!response?.success) return
      const updated = response.product
      const publicId = variables.publicId
      queryClient.setQueryData(dataQueryKey, (old) => {
        if (!old?.products) return old
        return {
          ...old,
          products: old.products.map((p) =>
            p.public_id === publicId ? updated : p
          ),
        }
      })
    },
    onSettled: (_data, _error, variables) => {
      setPendingRowIds((prev) => {
        const next = new Set(prev)
        next.delete(variables.publicId)
        return next
      })
    },
  })

  const handleDeleteAsin = useCallback(async (asin) => {
    try {
      await dispatch(deleteSingleAsin(asin)).unwrap()
      setHiddenAsins((prev) => (
        prev.includes(asin)
          ? prev
          : [...prev, asin]
      ))
      queryClient.setQueryData(dataQueryKey, (prev) => {
        if (!prev?.products) return prev
        return {
          ...prev,
          products: prev.products.filter(product => product.asin !== asin),
        }
      })
    } catch (error) {
      console.error('Fehler beim Löschen des Preisweckers:', error)
      alert('Fehler beim Löschen des Preisweckers. Bitte versuchen Sie es erneut.')
    }
  }, [dispatch, queryClient, dataQueryKey])

  const allColumns = useMemo(() => [
    application !== 'pricealerts' && application !== 'a2a' && application !== 'spy' && !isWholesaleMode && {
      id: 'supplier_img',
      header: 'Store img',
      accessorKey: 'supplier_img',
      enableResizing: false,
      className: styles.imageZoomCell,
      cell: ({ getValue }) =>
        <div className={classNames('w-28 h-28', styles.imageZoomWrapper)}>
          <img
            src={getValue()}
            className={classNames('w-full h-full object-cover rounded-md', styles.imageZoomImg)}
          />
        </div>
    },
    {
      id: 'amz_img',
      header: 'Amazon img',
      accessorKey: 'amz_img',
      enableResizing: false,
      className: styles.imageZoomCell,
      cell: function MismatchImageCell({ getValue, row }) {
        const [badgeHover, setBadgeHover] = useState(false)
        return (
          <div className={classNames('w-28 h-28 relative', styles.imageContainer)}>
            <div className={classNames('w-full h-full', styles.imageZoomWrapper, badgeHover && styles.zoomDisabled)}>
              <img
                src={getValue()}
                className={classNames('w-full h-full object-cover rounded-md', styles.imageZoomImg)}
              />
            </div>
            {row.original.mismatch_warning && (
              <Tippy
                placement='top'
                delay={0}
                duration={[200, 0]}
                content={<div className='text-xs'>Achtung: erhöhte Mismatch-Gefahr</div>}
              >
                <span
                  className={classNames('absolute top-0 right-0 bg-amber-500 text-white rounded-bl-md rounded-tr-md p-0.5 cursor-help z-10', styles.mismatchBadge)}
                  onMouseEnter={() => setBadgeHover(true)}
                  onMouseLeave={() => setBadgeHover(false)}
                >
                  <MismatchIcon />
                </span>
              </Tippy>
            )}
          </div>
        )
      }
    },
    {
      id: 'actions',
      header: 'Befehle',
      enableResizing: false,
      cell: ({ row }) => {
        const rowAsin = resolveAsin(row.original)
        const mismatchActive = resolveMismatchActive(row.original.mismatch)
        const defaultRegion = (Array.isArray(regions) && regions.length
          ? regions[0]
          : (Object.keys(row.original?.regions || {})[0] || 'de'))
        const buyprice = row.original?.buyprice || row.original?.regions?.[defaultRegion]?.buyprice || ''
        const cost = row.original?.cost || row.original?.regions?.[defaultRegion]?.cost || buyprice
        const amazonHref = isWholesaleMode
          ? (row.original?.regions?.[defaultRegion]?.amz_url
              || row.original?.amz_url
              || (rowAsin ? `https://www.amazon.${defaultRegion}/dp/${rowAsin}` : undefined))
          : buildDealsDirektLink(rowAsin, defaultRegion, buyprice, cost)
        const supplierHref = buildSupplierProductUrl(supplierPrefix, row.original)

        return (
          <div className='flex justify-center'>
            <div className='grid grid-cols-2 gap-1 w-fit'>
              <Tippy content='Amazon öffnen' placement='top'>
                <a
                  href={amazonHref}
                  target='_blank'
                  className='w-6 h-6 bg-primary text-white dark:bg-secondary dark:text-primary flex items-center justify-center rounded-md'
                >
                  <AmazonIcon className='w-4 h-4' />
                </a>
              </Tippy>
              <Tippy content={String(supplierPrefix || '').toLowerCase() === 'eany' ? 'Eany & Amazon öffnen' : 'Store & Amazon öffnen'} placement='top'>
                <button
                  onClick={() => {
                    if (supplierHref) window.open(supplierHref, '_blank')
                    regions.forEach(region => {
                      let regionData = row.original.regions?.[region]
                      if (regionData?.amz_url) {
                        if (isWholesaleMode) {
                          window.open(regionData.amz_url)
                        } else {
                          const regionAsin = regionData?.asin ?? row.original.asin
                          const regionBuyprice = regionData?.buyprice || row.original?.buyprice || ''
                          const regionCost = regionData?.cost || row.original?.cost || regionBuyprice
                          const target = buildDealsDirektLink(regionAsin, region, regionBuyprice, regionCost)
                          if (target) window.open(target)
                        }
                      }
                    })
                  }}
                  className='w-6 h-6 bg-primary text-white dark:bg-secondary dark:text-primary flex items-center justify-center rounded-md'
                >
                  <ExportIcon className='w-4 h-4' />
                </button>
              </Tippy>
              <Tippy content='A1 öffnen' placement='top'>
                <a
                  href={row.original.a1_url}
                  target='_blank'
                  className='w-6 h-6 bg-primary flex items-center justify-center rounded-md'
                >
                  <A1Icon className='w-5 h-5 dark:invert' />
                </a>
              </Tippy>
              <Tippy content='Selleramp öffnen' placement='top'>
                <a
                  href={`https://sas.selleramp.com/sas/lookup/?asin=${row.original.asin}&sas_cost_price=${row.original.buyprice}`}
                  target='_blank'
                  className='w-6 h-6 bg-primary text-white dark:bg-secondary dark:text-primary flex items-center justify-center rounded-md'
                >
                  <SasIcon className='w-6 h-6 [--sas-bg:#3d84ff] [--sas-text:#ffffff] dark:[--sas-bg:#0a241e] dark:[--sas-text:#2effc0]' />
                </a>
              </Tippy>
              {application === 'pricealerts' && row.original.supplier_url && (
                <Tippy content='Supplier zur Blacklist hinzufügen' placement='top'>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      
                      const url = row.original.supplier_url;
                      const supplierNameRaw = row?.original?.supplier_name;
                      const supplierName = typeof supplierNameRaw === 'string' ? supplierNameRaw.trim() : '';
                      
                      // Falls kein supplier_name vorhanden, versuche aus URL zu extrahieren
                      let finalSupplierName = supplierName;
                      if (!finalSupplierName || finalSupplierName === 'N/A') {
                        try {
                          const parsed = new URL(url);
                          let extractedHost = parsed.hostname.replace(/^www\./, '');
                          
                          // Für dealsdirekt die Ziel-URL extrahieren
                          if (extractedHost === 'dealsdirekt.de') {
                            const target = parsed.searchParams.get('url');
                            if (target) {
                              try {
                                extractedHost = new URL(target).hostname.replace(/^www\./, '');
                              } catch {
                                // ignore
                              }
                            }
                          }
                          finalSupplierName = extractedHost;
                        } catch {
                          // ignore
                        }
                      }
                      
                      if (finalSupplierName) {
                        addSupplierToBlacklistMutation.mutate({
                          filter_id: 'supplier',
                          filter_type: '∉',
                          attribute: finalSupplierName
                        });
                      }
                    }}
                    disabled={addSupplierToBlacklistMutation.isPending}
                    className='w-6 h-6 bg-primary text-white dark:bg-secondary dark:text-primary flex items-center justify-center rounded-md disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <svg className='w-4 h-4' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      {/* Einkaufswagen */}
                      {/* Griff links oben */}
                      <path d='M 4 8 L 2 4' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
                      {/* Wagenkorb - Rechteck ohne Gitter */}
                      <rect x='4' y='8' width='14' height='10' stroke='currentColor' strokeWidth='1.5' fill='none' />
                      {/* Räder - vorne rechts und hinten links */}
                      <circle cx='16' cy='20' r='2' fill='currentColor' />
                      <circle cx='6' cy='20' r='2' fill='currentColor' />
                    </svg>
                  </button>
                </Tippy>
              )}
                <Tippy content={mismatchActive ? 'Deal als Mismatch entfernen' : 'Deal als Mismatch markieren'} placement='top'>
                  <button
                    type='button'
                    onClick={() => handleMismatchToggle(row.original)}
                    disabled={rowAsin ? mismatchPendingAsins[rowAsin] : false}
                    className={classNames(
                      'w-6 h-6 bg-primary dark:bg-secondary dark:text-primary text-white flex items-center justify-center rounded-md',
                      'disabled:opacity-50 disabled:cursor-not-allowed'
                    )}
                  >
                    <MismatchIcon />
                  </button>
                </Tippy>
              {!isWholesaleMode && (
                <Tippy content={row.original.favorite ? 'Deal aus Favoriten entfernen' : 'Deal als Favorit markieren'} placement='top'>
                  <button
                    type='button'
                    onClick={() => handleFavoriteToggle(row.original)}
                    disabled={rowAsin ? favoritePendingAsins[rowAsin] : false}
                    className={classNames(
                      'w-6 h-6 flex items-center justify-center rounded-md transition border',
                      'bg-white text-primary border-primary/40 hover:bg-primary/10',
                      'dark:bg-secondary dark:text-primary dark:border-transparent',
                      'disabled:opacity-50 disabled:cursor-not-allowed'
                    )}
                  >
                    <FavoriteStar active={!!row.original.favorite} />
                  </button>
                </Tippy>
              )}
              {isWholesaleMode && (
                <Tippy content='Deal als Favorit markieren' placement='top'>
                  <button
                    type='button'
                    onClick={() => handleFavoriteModalOpen(row.original)}
                    className={classNames(
                      'w-6 h-6 flex items-center justify-center rounded-md transition border',
                      'bg-white text-primary border-primary/40 hover:bg-primary/10',
                      'dark:bg-secondary dark:text-primary dark:border-transparent'
                    )}
                  >
                    <FavoriteStar active={!!row.original.favorite} />
                  </button>
                </Tippy>
              )}
              {application === 'pricealerts' && !resolvePregen(row.original?.is_pregen) && (
                <Tippy content='Preiswecker löschen' placement='top'>
                  <button
                    onClick={async () => {
                      try {
                        await dispatch(deleteSingleAsin(row.original.asin)).unwrap()
                        setHiddenAsins((prev) => (
                          prev.includes(row.original.asin)
                            ? prev
                            : [...prev, row.original.asin]
                        ))
                        queryClient.setQueryData(dataQueryKey, (prev) => {
                          if (!prev?.products) return prev
                          return {
                            ...prev,
                            products: prev.products.filter(product => product.asin !== row.original.asin),
                          }
                        })
                      } catch (error) {
                        console.error('Fehler beim Löschen des Preisweckers:', error)
                        alert('Fehler beim Löschen des Preisweckers. Bitte versuchen Sie es erneut.')
                      }
                    }}
                    className='w-6 h-6 bg-primary dark:bg-secondary dark:text-primary text-white flex items-center justify-center rounded-md'
                  >
                    <TrashIcon className='w-4 h-4' />
                  </button>
                </Tippy>
              )}
            </div>
          </div>
        )
      },
    },
    chartColumn,
    {
      id: 'flags',
      enableResizing: false,
      cell: ({ row }) => <div className='flex flex-col justify-between'>
        {regions.map(region =>
          <div key={region} className='h-8 flex items-center'>
            <a
              href={isWholesaleMode
                ? (row.original?.regions?.[region]?.amz_url
                    || row.original?.amz_url
                    || (row.original.asin ? `https://www.amazon.${region}/dp/${row.original.asin}` : undefined))
                : buildDealsDirektLink(row.original.asin, region, row.original?.buyprice || row.original?.regions?.[region]?.buyprice || '', row.original?.cost || row.original?.regions?.[region]?.cost || row.original?.buyprice || '')}

              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer hover:opacity-80 transition-opacity"
            >
              <Flag country={region.toUpperCase()} size={16} />
            </a>
          </div>)
        }
      </div>
    },
    {
      id: 'margin',
      header: () => (
        <div className='flex items-center gap-1 cursor-pointer'>
          <span className={!use30d ? '' : 'opacity-50'}>Marge</span>
          <ExchangeIcon className='w-3 h-3' onClick={e => {
            e.stopPropagation()
            e.preventDefault()
            setUse30d(v => !v)
          }} />
          <span className={use30d ? '' : 'opacity-50'}>30D</span>
        </div>
      ),
      cell: ({ row }) => {
        const publicId = row.original?.public_id
        const isPending = publicId && pendingRowIds.has(publicId)
        return (
          <div className='flex flex-col justify-between items-center'>
            {regions.map(region => {
              if (isPending) {
                return (
                  <div key={region} className='h-8 flex items-center justify-center w-full'>
                    <SkeletonPill />
                  </div>
                )
              }
              let value = use30d
                ? row.original.regions[region]?.margin30
                : row.original.regions[region]?.margin
              return value !== undefined && value !== null ?
                <div key={region} className='h-8 flex items-center justify-center w-full'>
                  <Pill
                    color={
                      value < 4 ? 'red' :
                        value < 7 ? 'orange' :
                          'green'
                    }
                  >{`${value}%`}</Pill>
                </div> :
                <div key={region} className='h-8 flex items-center justify-center w-full'>
                  <Pill color='gray'>-</Pill>
                </div>
            })}
          </div>
        )
      }
    },
    isWholesaleMode && {
      id: 'packageQuantity',
      header: 'Einheiten',
      cell: ({ row }) => {
        const publicId = row.original?.public_id
        const firstRegion = regions[0]
        const regionData = row.original?.regions?.[firstRegion] || {}
        const value = regionData.packageQuantity ?? 1
        const userValue = regionData.packageQuantityUser ?? null
        const canEdit = Boolean(isWholesaleMode && !favoriteId && publicId)
        const isPending = canEdit && pendingRowIds.has(publicId)
        return (
          <EditablePackageQuantityCell
            value={value}
            userValue={userValue}
            editable={canEdit}
            isPending={isPending}
            onSubmit={(newValue) => {
              updateWholesaleEntryMutation.mutate({
                listId,
                publicId,
                field: 'package_quantity',
                value: newValue,  // int oder null (= reset)
                regions,
              })
            }}
          />
        )
      }
    },
    {
      id: 'roi',
      header: () => (
        <div className='flex items-center gap-1 cursor-pointer'>
          <span className={!use30d ? '' : 'opacity-50'}>Roi</span>
          <ExchangeIcon className='w-3 h-3' onClick={e => {
            e.stopPropagation()
            e.preventDefault()
            setUse30d(v => !v)
          }} />
          <span className={use30d ? '' : 'opacity-50'}>30D</span>
        </div>
      ),
      cell: ({ row }) => {
        const publicId = row.original?.public_id
        const isPending = publicId && pendingRowIds.has(publicId)
        return (
          <div className='flex flex-col justify-between items-center'>
            {regions.map(region => {
              if (isPending) {
                return (
                  <div key={region} className='h-8 flex items-center justify-center w-full'>
                    <SkeletonPill />
                  </div>
                )
              }
              let value = use30d
                ? row.original.regions[region]?.roi30
                : row.original.regions[region]?.roi
              return value !== undefined && value !== null ?
                <div key={region} className='h-8 flex items-center justify-center w-full'>
                  <Pill
                    color={
                      value < 4 ? 'red' :
                        value < 7 ? 'orange' :
                          'green'
                    }
                  >{`${value}%`}</Pill>
                </div> :
                <div key={region} className='h-8 flex items-center justify-center w-full'>
                  <Pill color='gray'>-</Pill>
                </div>
            })}
          </div>
        )
      }
    },
    {
      id: 'profit',
      header: () => (
        <div className='flex items-center gap-1 cursor-pointer'>
          <span className={!use30d ? '' : 'opacity-50'}>Profit</span>
          <ExchangeIcon className='w-3 h-3' onClick={e => {
            e.stopPropagation()
            e.preventDefault()
            setUse30d(v => !v)
          }} />
          <span className={use30d ? '' : 'opacity-50'}>30D</span>
        </div>
      ),
      cell: ({ row }) => {
        const publicId = row.original?.public_id
        const isPending = publicId && pendingRowIds.has(publicId)
        return (
          <div className='flex flex-col justify-between items-center'>
            {regions.map(region => {
              if (isPending) {
                return (
                  <div key={region} className='h-8 flex items-center justify-center w-full'>
                    <SkeletonPill />
                  </div>
                )
              }
              let value = use30d
                ? row.original.regions[region]?.profit30
                : row.original.regions[region]?.profit
              let roiValue = use30d
                ? row.original.regions[region]?.roi30
                : row.original.regions[region]?.roi
              let color =
                roiValue < 4 ? 'red' :
                roiValue < 7 ? 'orange' :
                'green'
              return value !== undefined && value !== null ?
                <div key={region} className='h-8 flex items-center justify-center w-full'>
                  <Pill color={color}>
                    {`${currencyFormatter(value)}€`}
                  </Pill>
                </div> :
                <div key={region} className='h-8 flex items-center justify-center w-full'>
                  <Pill color='gray'>-</Pill>
                </div>
            })}
          </div>
        )
      }
    },
    {
      id: 'salesrank',
      header: 'BSR',
      enableResizing: false,
      cell: ({ row }) => <div className='flex flex-col justify-between items-center'>
        {regions.map(region => {
          let value = row.original.regions[region]?.salesrank;
          return value !== undefined && value !== null ?
            <div key={region} className='h-8 flex items-center w-full justify-center'>
              <Pill>
                {value}
              </Pill>
            </div> :
            <div key={region} className='h-8 flex items-center w-full justify-center'>
              <Pill color='gray'>-</Pill>
            </div>;
        })}
      </div>
    },
    {
      id: 'percent_bsr',
      header: 'BSR%',
      enableResizing: false,
      cell: ({ row }) => <div className='flex flex-col justify-between items-center'>
        {regions.map(region => {
          let value = row.original.regions[region]?.percent_bsr;
          return value !== undefined && value !== null ?
            <div key={region} className='h-8 flex items-center w-full justify-center'>
              <Pill>
                {value}%
              </Pill>
            </div> :
            <div key={region} className='h-8 flex items-center w-full justify-center'>
              <Pill color='gray'>-</Pill>
            </div>;
        })}
      </div>
    },
    {
      id: 'asin',
      header: 'Asin',
      accessorKey: 'asin',
      cell: ({ getValue, row }) => {
        const asin = getValue();
        const region = (Array.isArray(regions) && regions.length
          ? regions[0]
          : (Object.keys(row.original?.regions || {})[0] || 'de'))
        const buyprice = row.original?.buyprice || row.original?.regions?.[region]?.buyprice || ''
        const cost = row.original?.cost || row.original?.regions?.[region]?.cost || buyprice
        const url = isWholesaleMode
          ? (row.original?.regions?.[region]?.amz_url
              || row.original?.amz_url
              || (asin ? `https://www.amazon.${region}/dp/${asin}` : undefined))
          : buildDealsDirektLink(asin, region, buyprice, cost);
        return (
          <Center>
            <a href={url} target="_blank" rel="noopener noreferrer" className="no-underline">
              <Pill className="cursor-pointer">{asin}</Pill>
            </a>
          </Center>
        )
      }
    },
    isWholesaleMode && {
      id: 'product_id_list',
      header: 'Product ID',
      accessorKey: 'product_id_list',
      cell: ({ getValue, row }) => {
        const value = getValue() ?? '-'
        const supplierHref = buildSupplierProductUrl(supplierPrefix, row.original)
        if (!supplierHref) {
          return <Center><Pill>{value}</Pill></Center>
        }

        return (
          <Center>
            <a href={supplierHref} target="_blank" rel="noopener noreferrer" className="no-underline">
              <Pill className="cursor-pointer text-primary">{value}</Pill>
            </a>
          </Center>
        )
      }
    },
    !isWholesaleMode && {
      id: 'supplier_url',
      header: 'Supplier',
      accessorKey: 'supplier_url',
      enableResizing: false,
      cell: ({ getValue, row }) => {
        const url = getValue();
        if (!url) return null;

        const supplierNameRaw = row?.original?.supplier_name;
        const supplierName = typeof supplierNameRaw === 'string' ? supplierNameRaw.trim() : '';
        const hasSupplierName = supplierName && supplierName !== 'N/A';

        let host = '';
        let link = url;
        try {
          const parsed = new URL(url);
          const rawHost = parsed.hostname.replace(/^www\./, '');
          host = rawHost;

          // Für Weiterleitungen von dealsdirekt die eigentliche Ziel-URL als Name anzeigen
          if (host === 'dealsdirekt.de') {
            const target = parsed.searchParams.get('url');
            if (target) {
              try {
                host = new URL(target).hostname.replace(/^www\./, '');
              } catch {
                // ignore fallback to dealsdirekt host
              }
            }
          }

          // Amazon-Supplier-Links über dealsdirekt weiterleiten, falls noch nicht erfolgt
          if (host.startsWith('amazon.') && rawHost !== 'dealsdirekt.de') {
            const buyprice = row.original?.buyprice || ''
            const cost = row.original?.cost || buyprice
            link = `https://dealsdirekt.de/go.html?url=${encodeURIComponent(url)}`
            if (buyprice) {
              link += `&bbp-buyprice=${encodeURIComponent(buyprice)}`
            }
            if (cost) {
              link += `&sas_cost_price=${encodeURIComponent(cost)}`
            }
          }
        } catch {
          // ignore invalid URL, leave host empty
        }

        return (
          <a href={link} target="_blank" rel="noopener noreferrer" className="w-full flex justify-center">
            <Pill className="cursor-pointer text-primary">{hasSupplierName ? supplierName : host}</Pill>
          </a>
        );
      }
    },
    {
      id: 'name',
      header: 'Title',
      accessorKey: 'name',
      cell: ({ getValue }) => <Center><Pill>{getValue()}</Pill></Center>
    },
    isWholesaleMode && {
      id: 'brand',
      header: 'Brand',
      accessorKey: 'brand',
      cell: ({ getValue }) => <Center><Pill>{getValue() || '-'}</Pill></Center>
    },
    {
      id: 'ean',
      header: 'EAN',
      accessorKey: 'ean',
      cell: ({ getValue, row }) => {
        const value = getValue() || '-'
        const supplierHref = buildSupplierProductUrl(supplierPrefix, row.original)
        if (!supplierHref) {
          return <Center><Pill>{value}</Pill></Center>
        }

        return (
          <Center>
            <a href={supplierHref} target="_blank" rel="noopener noreferrer" className="no-underline">
              <Pill className="cursor-pointer text-primary">{value}</Pill>
            </a>
          </Center>
        )
      }
    },
    isWholesaleMode && {
      id: 'extra_row',
      header: 'Optionale Spalte',
      accessorKey: 'extra_row',
      cell: ({ getValue }) => {
        const value = getValue()
        return <Center><Pill>{value ?? '-'}</Pill></Center>
      },
    },
    isWholesaleMode && {
      id: 'title_row',
      header: 'Lieferanten-Titel',
      accessorKey: 'title_row',
      cell: ({ getValue }) => {
        const value = getValue()
        return <Center><Pill>{value ?? '-'}</Pill></Center>
      },
    },
    {
      id: 'sellprice',
      header: () => (
        <div className='flex items-center gap-1 cursor-pointer'>
          <span className={!use30d ? '' : 'opacity-50'}>Sellprice</span>
          <ExchangeIcon className='w-3 h-3' onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            setUse30d(v => !v);
          }} />
          <span className={use30d ? '' : 'opacity-50'}>30D</span>
        </div>
      ),
      cell: ({ row }) => <div className='flex flex-col justify-between items-center'>
        {regions.map(region => {
          let value = use30d
            ? row.original.regions[region]?.sellprice30
            : row.original.regions[region]?.sellprice;
          if (value !== undefined && value !== null) {
            return (
              <div key={region} className='h-8 flex items-center w-full justify-center'>
                <Pill>
                  {currencyFormatter(value)}€
                </Pill>
              </div>
            )
          }

          return (
            <div key={region} className='h-8 flex items-center w-full justify-center'>
              <Pill color='gray'>-</Pill>
            </div>
          )
        })}
      </div>
    },
    application === 'spy' && {
      id: 'seller_name',
      header: 'Seller',
      accessorFn: (row) => {
        if (Array.isArray(row.tracked_sellers) && row.tracked_sellers.length > 0) {
          return row.tracked_sellers
            .map(([, name]) => name)
            .filter(Boolean)
            .join(', ')
        }

        return row.seller_name ?? row.seller ?? ''
      },
      cell: ({ row, getValue }) => {
        const trackedSellers = Array.isArray(row.original.tracked_sellers)
          ? row.original.tracked_sellers.filter((entry) => Array.isArray(entry) && (entry[0] || entry[1]))
          : []

        const sellerNames = trackedSellers.map(([, name]) => name).filter(Boolean)

        if (trackedSellers.length === 0) {
          const fallback = getValue() || '-'
          return <Center><Pill>{fallback || '-'}</Pill></Center>
        }

        const displayName = sellerNames[0] || getValue() || '-'
        const extraCount = sellerNames.length - 1
        const pillLabel = extraCount > 0 ? `${displayName} +${extraCount}` : displayName

        return (
          <Center>
            <Tippy
              placement='bottom'
              content={
                <div className='flex flex-col gap-1 text-xs'>
                  {trackedSellers.map(([id, name], index) => (
                    <span key={id || `${name}-${index}`}>
                      {name || '-'} {id ? `(${id})` : ''}
                    </span>
                  ))}
                </div>
              }
            >
              <span>
                <Pill>{pillLabel}</Pill>
              </span>
            </Tippy>
          </Center>
        )
      }
    },
    {
      id: 'confirmed_sales',
      header: 'Conf. Sales',
      accessorKey: 'confirmed_sales',
      cell: ({ row }) => <div className='flex flex-col justify-between items-center'>
        {regions.map(region => {
          let value = row.original.regions[region]?.confirmed_sales
          return value !== undefined && value !== null ?
            <div key={region} className='h-8 flex items-center w-full justify-center'>
              <Pill>
                {value}
              </Pill>
            </div> :
            <div key={region} className='h-8 flex items-center w-full justify-center'>
              <Pill color='gray'>-</Pill>
            </div>
        })}
      </div>
    },
    isWholesaleMode && {
      id: 'estimated_sales',
      header: 'Est. Sales',
      accessorKey: 'estimated_sales',
      cell: ({ row }) => <div className='flex flex-col justify-between items-center'>
        {regions.map(region => {
          const value =
            row.original?.regions?.[region]?.estimated_sales ??
            row.original?.estimated_sales ??
            null
          if (value !== undefined && value !== null) {
            const color =
              value < 5 ? 'red' :
              value < 30 ? 'orange' :
              'green'
            const displayValue = value < 5 ? '< 5' : value
            return (
              <div key={region} className='h-8 flex items-center w-full justify-center'>
                <Pill color={color}>{displayValue}</Pill>
              </div>
            )
          }
          return (
            <div key={region} className='h-8 flex items-center w-full justify-center'>
              <Pill color='gray'>-</Pill>
            </div>
          )
        })}
      </div>
    },

    {
      id: 'salesdrops',
      header: 'Salesdrops',
      accessorKey: 'salesdrops',
      cell: ({ row }) => <div className='flex flex-col justify-between items-center'>
        {regions.map(region => {
          let value = row.original.regions[region]?.salesdrops
          return value !== undefined && value !== null ?
            <div key={region} className='h-8 flex items-center w-full justify-center'>
              <Pill>
                {value}
              </Pill>
            </div> :
            <div key={region} className='h-8 flex items-center w-full justify-center'>
              <Pill color='gray'>-</Pill>
            </div>
        })}
      </div>
    },
    isWholesaleMode && {
      id: 'offers',
      header: 'Offers',
      accessorKey: 'offers',
      cell: ({ row }) => <div className='flex flex-col justify-between items-center'>
        {regions.map(region => {
          const value =
            row.original?.regions?.[region]?.offers ??
            row.original?.offers ??
            null
          return value !== undefined && value !== null ?
            <div key={region} className='h-8 flex items-center w-full justify-center'>
              <Pill>
                {value}
              </Pill>
            </div> :
            <div key={region} className='h-8 flex items-center w-full justify-center'>
              <Pill color='gray'>-</Pill>
            </div>
        })}
      </div>
    },
    {
      id: 'variations',
      header: 'Variations',
      accessorKey: 'variations',
      cell: ({ row }) => <div className='flex flex-col justify-between items-center'>
        {regions.map(region => {
          const value =
            row.original?.regions?.[region]?.variations ??
            null
          return value !== undefined && value !== null && value !== 'N/A' ?
            <div key={region} className='h-8 flex items-center w-full justify-center'>
              <Pill>
                {value}
              </Pill>
            </div> :
            <div key={region} className='h-8 flex items-center w-full justify-center'>
              <Pill color='gray'>-</Pill>
            </div>
        })}
      </div>
    },
    isWholesaleMode && {
      id: 'seasonality',
      header: 'Seasonality',
      accessorFn: (row) => row.seasonality,
      enableResizing: false,
      cell: ({ row }) => (
        <div className='flex flex-col justify-between items-center'>
          {regions.map(region => {
            const seasonalityValue =
              row.original?.regions?.[region]?.seasonalityDatasets ??
              row.original?.seasonality
            return (
              <div key={region} className='h-8 flex items-center w-full justify-center'>
                <SeasonalityCell value={seasonalityValue} />
              </div>
            )
          })}
        </div>
      )
    },
    isWholesaleMode && {
      id: 'velocity_price',
      header: 'Velocity Price',
      accessorFn: (row) => row.velocity_price,
      enableResizing: false,
      cell: ({ row }) => (
        <div className='flex flex-col justify-between items-center'>
          {regions.map(region => {
            const velocityValue =
              row.original?.regions?.[region]?.computedVelocityPrice ??
              row.original?.regions?.[region]?.velocity_price ??
              row.original?.velocity_price
            return (
              <div key={region} className='h-8 flex items-center w-full justify-center'>
                <VelocityPriceCell value={velocityValue} />
              </div>
            )
          })}
        </div>
      )
    },
    isWholesaleMode && {
      id: 'ai_analysis',
      header: 'AI Analysis',
      enableResizing: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div className='flex flex-col items-center gap-2 py-1'>
          {regions.map(region => {
            const value = resolveRegionAIAnalysis(row.original?.regions?.[region])
            return (
              <div key={region} className='flex items-center w-full justify-center'>
                <AIAnalysisTag analysis={value} />
              </div>
            )
          })}
        </div>
      )
    },
    isWholesaleMode && {
      id: 'ai_insight',
      header: 'AI Insight',
      enableResizing: false,
      cell: ({ row }) => (
        <div className='flex flex-col items-center gap-2 py-1'>
          {regions.map(region => {
            const regionAsin = row.original?.regions?.[region]?.asin ?? row.original?.asin
            const regionCost =
              row.original?.regions?.[region]?.cost ??
              row.original?.regions?.[region]?.buyprice ??
              row.original?.cost ??
              row.original?.buyprice
            return (
              <div key={region} className='flex items-center w-full justify-center'>
                <AICheckButton asin={regionAsin} region={region} cost={regionCost} />
              </div>
            )
          })}
        </div>
      )
    },
    {
      id: 'buyprice',
      header: 'Buyprice',
      accessorKey: 'buyprice',
      cell: ({ getValue, row }) => {
        const publicId = row.original?.public_id
        const canEdit = Boolean(isWholesaleMode && !favoriteId && publicId)
        const isPending = canEdit && pendingRowIds.has(publicId)
        return (
          <EditableBuypriceCell
            value={getValue()}
            editable={canEdit}
            isPending={isPending}
            onSubmit={(newValue) => {
              updateWholesaleEntryMutation.mutate({
                listId,
                publicId,
                field: 'price',
                value: newValue,
                regions,
              })
            }}
          />
        )
      }
    },
    !isWholesaleMode && {
      id: 'timestamp',
      header: 'Timestamp',
      accessorKey: 'timestamp',
      cell: ({ getValue }) => {
        const formatted = formatBerlinPingTimestamp(getValue())
        const parts = typeof formatted === 'string' ? formatted.split(' | ') : []
        const isSplit = parts.length === 2
        return (
          <Center>
            <Pill className={isSplit ? 'py-1 whitespace-normal' : undefined}>
              {parts.length === 2 ? (
                <span className="flex flex-col leading-tight">
                  <span>{parts[0]}</span>
                  <span>{parts[1]}</span>
                </span>
              ) : (
                formatted
              )}
            </Pill>
          </Center>
        )
      }
    },
  ].filter(Boolean), [application, chartColumn, dataQueryKey, handleFavoriteModalOpen, isWholesaleMode, queryClient, regions, use30d, addSupplierToBlacklistMutation, buildDealsDirektLink, resolveAsin, resolveMismatchActive, resolvePregen, favoriteId, listId, supplierPrefix, pendingRowIds, updateWholesaleEntryMutation])

  const columns = useMemo(() =>
    allColumns.filter(column => !hiddenColumnIds.includes(column.id)), [allColumns, hiddenColumnIds])
  let { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: dataQueryKey,
    queryFn: () => {
      if (isWholesaleMode) {
        if (supplierPrefix) {
          return getB2BSupplierProductsAPI(
            supplierPrefix,
            regions,
            search,
            sort.sortBy,
            sort.sortType,
            pagination.pageIndex,
            pagination.pageSize
          )
        }
        return getWholesaleProductsAPI(
          listId,
          favoriteId,
          search,
          sort.sortBy,
          sort.sortType,
          pagination.pageIndex,
          pagination.pageSize,
          regions
        )
      }

      return getStoreSearchAPI(application, regions, search, sort.sortBy, sort.sortType, pagination.pageIndex, pagination.pageSize)
    },
    placeholderData: (prev) => prev,
    staleTime: 60000,
    // Pausiert, solange Filter-Writes laufen -- siehe pendingFilterWrites oben.
    enabled: pendingFilterWrites === 0 && !filterPanelOpen && !pendingFilterPageReset,
  })

  // Bei offenem Filter-Panel laeuft die Daten-Schicht unveraendert weiter und
  // die Anzeige bleibt LIVE (ehrlich): ein beim Oeffnen bereits laufender Fetch
  // wird NICHT gecancelt -- wird er fertig, erscheinen die Produkte auch hinterm
  // Panel statt eines eingefrorenen Skeletons. Neue Requests entstehen waehrend
  // der Panel-Session trotzdem keine: Edits sind silent (kein Daten-Invalidate),
  // die Query ist pausiert (enabled unten), der Reload kommt gebuendelt beim
  // Schliessen. Cancel+Restart wurde bewusst verworfen (Server rechnet
  // gecancelte Queries trotzdem zu Ende -> Request-Spam bei Open/Close).
  const isRefreshing = (isFetching || pendingDataFilterWrites > 0) && data !== undefined

  // KEIN Prefetch der naechsten Seite: der Prefetch-Key passte nicht zum Haupt-Key
  // (zusaetzliches buyRegions) -> nie ein Cache-Hit, jede Seite ging doppelt raus.
  // Entscheidung: strikt 1 Request pro Seite; placeholderData haelt die alte Seite
  // sichtbar. (ProductTable behaelt seinen Prefetch, dort spiegelt der Key korrekt.)

  useEffect(() => {
    if (!isWholesaleMode) {
      return
    }

    if (controlledRegionsNormalized && controlledRegionsNormalized.length) {
      return
    }

    if (!data) {
      return
    }

    const derived = deriveRegionsFromData(data)

    if (!derived.length) {
      if (internalRegions.length) {
        setInternalRegions([])
      }
      return
    }

    if (!arraysEqual(internalRegions, derived)) {
      setInternalRegions(derived)
    }
  }, [controlledRegionsNormalized, data, internalRegions, isWholesaleMode])

  useEffect(() => {
    setHiddenAsins(prev => (prev.length ? [] : prev))
  }, [application, regions, search, sort.sortBy, sort.sortType, pagination.pageIndex, pagination.pageSize])

  const filteredProducts = useMemo(() => {
    let products = data?.products || []

    if (isWholesaleMode && products.length) {
      const defaultRegionKey = typeof data?.region === 'string' ? data.region.toLowerCase() : undefined

      products = products.map(product => {
        const supplierUrl = buildSupplierProductUrl(supplierPrefix, product)
        const rawRegions = Object.entries(product?.regions || {})
        const processedRegions = {}
        const normalizedRegionKeys = []

        rawRegions.forEach(([regionKeyRaw, regionValueRaw]) => {
          const normalizedKey = normalizeRegionKey(regionKeyRaw)
          if (!normalizedRegionKeys.includes(normalizedKey)) {
            normalizedRegionKeys.push(normalizedKey)
          }

          const regionData = regionValueRaw || {}
          const seasonalityDatasets = createSeasonalityDatasets(product, normalizedKey, regionData)
          const computedVelocityPrice = resolveVelocityPrice(seasonalityDatasets, regionData)

          const enhancedRegionData = {
            ...regionData,
            seasonalityDatasets,
            computedVelocityPrice,
          }

          processedRegions[normalizedKey] = enhancedRegionData
          if (normalizedKey !== regionKeyRaw) {
            processedRegions[regionKeyRaw] = enhancedRegionData
          }
        })

        if (!normalizedRegionKeys.length) {
          const fallbackSeasonality = createSeasonalityDatasets(product, 'global', null)
          return {
            ...product,
            supplier_url: supplierUrl ?? product?.supplier_url,
            regions: product?.regions || {},
            seasonality: fallbackSeasonality,
            velocity_price: computeVelocityPriceRange(fallbackSeasonality) ?? product?.velocity_price ?? null,
          }
        }

        const preferredRegionKey = (defaultRegionKey && processedRegions[defaultRegionKey])
          ? defaultRegionKey
          : normalizedRegionKeys[0]

        const preferredRegion = processedRegions[preferredRegionKey] ?? null
        const seasonalityDatasets = preferredRegion?.seasonalityDatasets ?? []
        const velocityPrice = preferredRegion?.computedVelocityPrice ??
          computeVelocityPriceRange(seasonalityDatasets) ??
          product?.velocity_price ??
          null

        return {
          ...product,
          supplier_url: supplierUrl ?? product?.supplier_url,
          extra_row: product?.extra_row ?? preferredRegion?.extra_row ?? null,
          regions: processedRegions,
          seasonality: seasonalityDatasets,
          velocity_price: velocityPrice,
        }
      })
    }

    if (!hiddenAsins.length) {
      return products
    }
    let hiddenSet = new Set(hiddenAsins)
    return products.filter(product => !hiddenSet.has(product.asin))
	  }, [data?.products, data?.region, hiddenAsins, isWholesaleMode, supplierPrefix])

  const favoriteModalRegion = useMemo(() => {
    if (!favoriteModalProduct) {
      return null
    }
    return favoriteModalProduct?.primaryRegion
      || (Array.isArray(regions) && regions.length ? regions[0] : null)
      || (typeof data?.region === 'string' ? data.region : null)
  }, [data?.region, favoriteModalProduct, regions])

  let scrollViewRef = useRef(null)
  const scrollPageToTop = useCallback(() => {
    onPageViewportReset?.()
    scrollToTopFrom(scrollViewRef.current)
  }, [onPageViewportReset])

  // Beim Seitenwechsel/PageSize-Wechsel zurueck nach oben. Bei vorgeladener Seite
  // tauscht react-table die Rows synchron NACH dem scroll -> Browser-Scroll-
  // Anchoring zieht die Position zurueck. scrollTop=0 sofort + naechster Frame;
  // overflowAnchor:'none' am Container (unten) verhindert das Zurueckziehen ganz.
  useEffect(() => {
    scrollPageToTop()
    const id = requestAnimationFrame(scrollPageToTop)
    return () => cancelAnimationFrame(id)
  }, [pagination.pageIndex, pagination.pageSize, scrollPageToTop])

  const storagePrefix = storageKeyPrefix ?? application

  const enableCardView = useMemo(
    () => ['a2a', 'pricealerts', 'spy'].includes(application),
    [application]
  )

  let [viewMode, setViewMode] = useLocalStorageState(`${storagePrefix}-view-mode`, {
    defaultValue: 'table',
  })

  useEffect(() => {
    if (!enableCardView && viewMode === 'cards') {
      setViewMode('table')
    }
  }, [enableCardView, viewMode, setViewMode])

  let [columnOrder, setColumnOrder] = useLocalStorageState(`${storagePrefix}-col-order`)
  let [columnSizing, setColumnSizing] = useLocalStorageState(`${storagePrefix}-col-sizing`, {
    defaultValue: {}
  })
  const resolvedSettingsModule = columnSettingsModule ?? application
  let [settingsLoaded, setSettingsLoaded] = useState(false)

  useEffect(() => {
    setSettingsLoaded(false)
  }, [resolvedSettingsModule])

  useColumnSettingsLoader({
    module: resolvedSettingsModule,
    setColumnOrder,
    setHiddenColumnIds,
    setColumnSizing,
    onLoaded: () => setSettingsLoaded(true),
  })

  useEffect(() => {
    if (!isWholesaleMode) {
      return
    }

    setColumnOrder((prev) => {
      if (!Array.isArray(prev)) {
        return prev
      }

      const hasEstimated = prev.includes('estimated_sales')
      const confirmedIndex = prev.indexOf('confirmed_sales')

      if (!hasEstimated) {
        if (confirmedIndex === -1) {
          return [...prev, 'estimated_sales']
        }

        const next = [...prev]
        next.splice(confirmedIndex + 1, 0, 'estimated_sales')
        return next
      }

      const estimatedIndex = prev.indexOf('estimated_sales')

      if (confirmedIndex !== -1 && estimatedIndex !== confirmedIndex + 1) {
        const next = prev.filter((id) => id !== 'estimated_sales')
        next.splice(confirmedIndex + 1, 0, 'estimated_sales')
        return next
      }

      return prev
    })
  }, [isWholesaleMode, setColumnOrder])

  // B2B: kein COUNT - has_next aus Response. Sonst echtes total_pages (WW).
  // data kann ein String ("not found") sein -> deshalb auf undefined pruefen,
  // nicht 'in'-Operator (wirft auf Non-Objects).
  const effectiveTotalPages = data?.has_next !== undefined
    ? (data.has_next ? pagination.pageIndex + 2 : pagination.pageIndex + 1)
    : (data?.total_pages || 0)

  // Frische Antwort da -> Totals-Klemme wieder loesen. Solange noch Filter-Writes
  // laufen (Query pausiert, isFetching false), Klemme halten -- sonst zeigen die
  // Totals kurz wieder den alten Stand, bevor der finale Fetch startet.
  useEffect(() => {
    if (totalsResetPending && !isFetching && pendingFilterWrites === 0) {
      setTotalsResetPending(false)
    }
  }, [totalsResetPending, isFetching, pendingFilterWrites])

  // Anzeige-Total: NUR die wirklich vom Server bekannte Zahl. Im B2B-Modus
  // (has_next ohne festen Total) und waehrend einer Reset-Phase ist die Zahl
  // unbekannt -> null, dann steht im Footer einfach nichts neben dem Chip.
  // (effectiveTotalPages oben bleibt -- useReactTable + Prefetch brauchen dort
  // weiter einen Zahlenwert.)
  const isHasNextMode = data?.has_next !== undefined
  const displayTotalPages = (totalsResetPending || isHasNextMode)
    ? null
    : (data?.total_pages || null)
  // Next-Button im B2B-Modus aus has_next steuern (sonst faellt die Bar auf
  // pageIndex < totalPages-1 zurueck, was bei totalPages=null disabled waere).
  const displayHasNext = (isHasNextMode && !totalsResetPending)
    ? Boolean(data.has_next)
    : undefined

  let table = useReactTable({
    columns,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    data: filteredProducts,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    onPaginationChange: setPagination,
    pageCount: effectiveTotalPages,
    state: {
      pagination,
      columnOrder,
      columnSizing,
    },
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
  })

  useEffect(() => {
    if (!Array.isArray(columnOrder)) {
      return
    }
    // Gegen ALLE Spalten abgleichen, nicht nur die sichtbaren: ausgeblendete
    // behalten ihre Position in der Order und tauchen beim Wiedereinblenden
    // an alter Stelle auf (statt ans Ende zu rutschen).
    const columnIds = allColumns.map((col) => col.id)
    const filtered = columnOrder.filter((id) => columnIds.includes(id))
    const missing = columnIds.filter((id) => !filtered.includes(id))
    const next = [...filtered, ...missing]
    if (!arraysEqual(next, columnOrder)) {
      setColumnOrder(next)
    }
  }, [columnOrder, allColumns, setColumnOrder])

  // Column drag & drop
  let handleDragStart = useCallback((e, columnId) => {
    e.dataTransfer.setData('text/plain', columnId)
    e.dataTransfer.effectAllowed = 'move'
  }, [])

  let handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])

  let handleDrop = useCallback((e, targetColumnId) => {
    e.preventDefault()
    let sourceColumnId = e.dataTransfer.getData('text/plain')

    if (sourceColumnId === targetColumnId) {
      return
    }

    let currentColumnOrder = table.getState().columnOrder ||
      table.getAllLeafColumns().map(column => column.id)

    let sourceIndex = currentColumnOrder.indexOf(sourceColumnId)
    let targetIndex = currentColumnOrder.indexOf(targetColumnId)

    // Pin-Guard gegen die Laenge der ORDER pruefen (enthaelt auch versteckte
    // IDs) -- gegen die sichtbare Spaltenzahl wuerden Drops am Ende blocken.
    if (sourceIndex === -1 || targetIndex === -1 || targetIndex >= currentColumnOrder.length - RIGHT_PINNED_COLUMNS) {
      return
    }

    let newColumnOrder = [...currentColumnOrder]
    newColumnOrder.splice(sourceIndex, 1)
    newColumnOrder.splice(targetIndex, 0, sourceColumnId)

    setColumnOrder(newColumnOrder)
  }, [table])

  let handleDragEnter = useCallback((e, columnId) => {
    setDragOverColumnId(columnId);
  }, [])

  let handleDragLeave = useCallback(() => {
    setDragOverColumnId(null)
  }, [])

  let [dragOverColumnId, setDragOverColumnId] = useState(null)
  // \ Column drag & drop

  useColumnSettingsSync({
    module: settingsLoaded ? resolvedSettingsModule : null,
    columnOrder,
    hiddenColumnIds,
    columnSizing,
  })

  // Skeleton-Austausch der GANZEN Tabelle nur beim Erstload (noch gar keine Daten).
  // Bei Refetches rendert nur der tbody Skeleton-Zeilen (s. unten) -- Header/Footer
  // bleiben stehen. isRefreshing (live + eingefroren, s. UI-Freeze oben) greift
  // auch schon waehrend laufender 'data'-Filter-Writes: Skeleton ab dem ersten
  // Klick, obwohl der Fetch erst nach der letzten Server-Bestaetigung startet.
  if (isLoading || data === undefined) {
    return (
      <>
        {viewMode === 'cards' && enableCardView ? (
          <ProductCardView loading outerScroll={outerScroll} />
        ) : (
          <SkeletonTable
            table={table}
            columns={columns}
            regions={regions}
            pagination={pagination}
            totalPages={displayTotalPages}
            hasNext={displayHasNext}
            setPagination={setPagination}
            outerScroll={outerScroll}
            onPageViewportReset={scrollPageToTop}
          />
        )}
      </>
    )
  }
  if (isError) {
    return <div className="error">Error: {error.message}</div>;
  }
  const renderTable = () => (
    <>
      {/* Ohne outerScroll: HORIZONTAL scrollt nur dieser Wrapper (overflow-x),
          Header/Filter der Page bleiben dabei ruhig stehen. shrink-0 ist der
          entscheidende Teil: ohne ihn schrumpft der Wrapper als Flex-Kind auf
          Viewport-Resthoehe und scrollt VERTIKAL intern -- dann staenden
          Header/Filter beim vertikalen Scrollen fest. Mit shrink-0 waechst er
          mit dem Inhalt und die Page-Section scrollt vertikal. */}
      <div
        key={pagination.pageIndex} // Neu: Key für Seitenwechsel
        ref={scrollViewRef}
        className={`relative border-t border-gray-200 dark:border-gray-700 ${outerScroll ? 'min-w-max' : 'overflow-x-auto shrink-0'}`}
        style={{ overflowAnchor: 'none' }}
      >
        <table
          className={`${styles.productGrid} min-w-full font-medium border-spacing-2 dark:text-white border-collapse w-max`}
        >
          <thead className='sticky top-0 z-10 dark:text-white'>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, at) => {
                  let resizeHandler = header.getResizeHandler()
                  return (
                    <th
                      key={header.id}
                      draggable={!header.column.getIsResizing() && at < columns.length - RIGHT_PINNED_COLUMNS ? true : undefined}
                      onDragStart={(e) => handleDragStart(e, header.id)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, header.id)}
                      onDragEnter={(e) => handleDragEnter(e, header.id)}
                      onDragLeave={handleDragLeave}
                      className={classNames('text-center', {
                        'drag-over': header.id === dragOverColumnId,
                        'bg-natural-300': header.column.getCanResize(),
                      })}
                      style={header.column.getCanResize() ? {
                        minWidth: header.getSize(),
                        maxWidth: header.getSize(),
                      } : {
                        width: '1px',
                        maxWidth: 'fit-content',
                      }}
                    >
                      <div className='inline-flex items-center justify-center px-3 rounded-full bg-neutral-100 dark:bg-neutral-800'>
                        <button
                          onClick={() => {
                            const canSort = SORT_COLUMNS.includes(header.id) && !supplierPrefix &&
                              (!WHOLESALE_ONLY_SORT_COLUMNS.has(header.id) || isWholesaleMode)
                            if (canSort) {
                              let sortBy = use30d ? (THIRTY_DAY_SORT_MAP[header.id] ?? header.id) : header.id
                              let sortType = 'ASC'
                              if (sort.sortBy === sortBy) {
                                sortType = (
                                  sort.sortType === 'ASC' ? 'DESC' :
                                    sort.sortType === 'DESC' ? undefined :
                                      'ASC'
                                )
                              }
                              setSort({ sortBy: sortType ? sortBy : undefined, sortType })
                            }
                          }}
                          className='inline-flex items-center gap-1'
                        >
                          {header.column.columnDef.header && flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {(() => {
                            const canSort = SORT_COLUMNS.includes(header.id) && !supplierPrefix &&
                              (!WHOLESALE_ONLY_SORT_COLUMNS.has(header.id) || isWholesaleMode)
                            if (!canSort) return null
                            const activeSortKey = use30d ? (THIRTY_DAY_SORT_MAP[header.id] ?? header.id) : header.id
                            if (sort.sortBy === activeSortKey) {
                              return sort.sortType === 'ASC'
                                ? <SortIcon className='d-inline w-3 h-3 rotate-180 text-primary' />
                                : <SortIcon className='w-3 h-3 text-primary' />
                            }
                            return <SortIcon className='w-3 h-3 opacity-15 text-black dark:text-white' />
                          })()}
                        </button>
                      </div>
                      {header.column.getCanResize() &&
                        <div
                          onDoubleClick={() => header.column.resetSize()}
                          onMouseDown={resizeHandler}
                          onTouchStart={resizeHandler}
                          className={classNames(styles.resizer, {
                            [styles.resizing]: header.column.getIsResizing(),
                          })}
                        />
                      }
                    </th>
                  )
                }
                )}
                <th className='min-w-20 w-full'></th>
              </tr>
            )
            )}
          </thead>
          <tbody>
            {isRefreshing
              // Refetch (Filter/Suche/Sort/Seite): Skeleton NUR im Body -- Header,
              // Spaltenbreiten, Container und Footer bleiben stehen, nichts springt.
              // Breiten der Skeleton-Zellen spiegeln die echten Zellen (columnSizing).
              ? Array.from({ length: pagination.pageSize }).map((_, at) => (
                <tr key={`skeleton-${at}`} className={classNames(at > 0 && 'border-t', 'border-gray-200 dark:border-gray-700')}>
                  {table.getVisibleLeafColumns().map(column => (
                    <td
                      key={column.id}
                      style={column.getCanResize() ? {
                        maxWidth: column.getSize(),
                        width: column.getSize(),
                      } : {}}
                      className={column.columnDef.className}
                    >
                      <SkeletonCell columnId={column.id} regions={regions} />
                    </td>
                  ))}
                </tr>
              ))
              : table.getRowModel().rows.map((row, at) => {
                return (
                  <tr key={row.id} className={classNames(at > 0 && 'border-t', 'border-gray-200 dark:border-gray-700')}>
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        style={cell.column.getCanResize() ? {
                          maxWidth: cell.column.getSize(),
                          width: cell.column.getSize(),
                        } : {}}
                        className={cell.column.columnDef.className}
                      >{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </>
  )

  const renderCards = () => (
    <ProductCardView
      products={filteredProducts}
      regions={regions}
      use30d={use30d}
      setUse30d={setUse30d}
      application={application}
      isPregenListFilterActive={false}
      isWholesaleMode={isWholesaleMode}
      handleMismatchToggle={handleMismatchToggle}
      handleFavoriteToggle={handleFavoriteToggle}
      handleFavoriteModalOpen={handleFavoriteModalOpen}
      handleDeleteAsin={handleDeleteAsin}
      favoritePendingAsins={favoritePendingAsins}
      mismatchPendingAsins={mismatchPendingAsins}
      addSupplierToBlacklistMutation={addSupplierToBlacklistMutation}
      resolveAsin={resolveAsin}
      resolveMismatchActive={resolveMismatchActive}
      pagination={pagination}
      data={data}
      totalPages={displayTotalPages}
      hasNext={displayHasNext}
      setPagination={setPagination}
      outerScroll={outerScroll}
      onPageViewportReset={scrollPageToTop}
    />
  )

  const handlePageChange = (page) => {
    scrollPageToTop()
    setPagination({ ...pagination, pageIndex: page })
  }

  const handlePageSizeChange = (size) => {
    scrollPageToTop()
    setPagination({ pageIndex: 0, pageSize: size })
  }

  const renderFooter = () => (
    <PaginationBar
      pageIndex={pagination.pageIndex}
      totalPages={displayTotalPages}
      hasNext={displayHasNext}
      setPage={handlePageChange}
      pageSize={pagination.pageSize}
      setPageSize={handlePageSizeChange}
    />
  )

  return (
    <>
      {viewMode === 'cards' && enableCardView
        ? (isRefreshing ? <ProductCardView loading outerScroll={outerScroll} /> : renderCards())
        : renderTable()}
      {!(viewMode === 'cards' && enableCardView) && renderFooter()}

      {favoriteModalProduct && (
        <CommonModal
          isOpen={!!favoriteModalProduct}
          closeModal={handleFavoriteModalClose}
        >
          <ProductFavorite
            product={favoriteModalProduct}
            region={favoriteModalRegion || undefined}
            onClose={handleFavoriteModalClose}
          />
        </CommonModal>
      )}
    </>
  )
}


// --- Keepa-Charts ----------------------------------------------------------
// Jedes Chart laedt primaer direkt von graph.keepa.com -- parallel, aber mit
// 200ms Abstand zwischen den Starts, damit es fuer Keepa nicht wie Spam
// aussieht. Blockt Keepa (meist 429; manchmal 200 + Fehler-PNG, erkennbar nur
// am Header "Screenshot-Status: FAIL"), wechseln nachfolgende Loads fuer 30s
// auf den eigenen /graph-CDN und NUR das betroffene Bild laedt sofort vom CDN
// nach. Fertig geladene Bilder werden nie umgeladen.

const keepaLimited = { until: 0 }

let nextKeepaSlot = 0
const claimKeepaSlot = () => {
  const at = Math.max(Date.now(), nextKeepaSlot)
  nextKeepaSlot = at + 200
  return at - Date.now()
}

// Keepa-Antwort vor dem Anzeigen pruefen: der Block kommt teils als 200 +
// Fehler-PNG, das ein <img> nicht von einem Chart unterscheiden kann. Die
// gepruefte URL laedt das <img> danach aus dem HTTP-Cache.
//
// Pro URL laeuft der Check nur EINmal, auch wenn dieselbe ASIN mehrfach auf
// der Seite steht: Duplikat-Zeilen haengen sich an dieselbe Promise (ein
// Slot, ein Request) und uebernehmen das Ergebnis. Nach Abschluss fliegt der
// Eintrag raus -- reines In-Flight-Dedupe, kein Cache. (CDN-Bilder dedupet
// der Browser selbst: gleiche <img>-URL = ein Request.)
const pendingKeepaChecks = new Map()
const checkKeepa = (url) => {
  let pending = pendingKeepaChecks.get(url)
  if (!pending) {
    pending = new Promise((resolve) => {
      setTimeout(async () => {
        try {
          const res = await fetch(url, { referrerPolicy: 'no-referrer' })
          if (!res.ok || res.headers.get('Screenshot-Status') === 'FAIL') throw new Error('keepa blocked')
          await res.blob()
          resolve(true)
        } catch {
          keepaLimited.until = Date.now() + 30000 // nachfolgende Loads -> CDN
          resolve(false)
        }
      }, claimKeepaSlot())
    })
    pendingKeepaChecks.set(url, pending)
    pending.finally(() => pendingKeepaChecks.delete(url))
  }
  return pending
}

// Die Tabellen scrollen in einem overflow-auto-Container, nicht im Viewport.
// Ein IntersectionObserver mit root=Viewport clippt alles unterhalb der
// Container-Kante weg -- rootMargin (Vorlauf) wirkt dann nie. Deshalb den
// tatsaechlich vertikal scrollenden Vorfahren als root nehmen.
const findScrollParent = (el) => {
  for (let node = el?.parentElement; node; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node)
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node
    }
  }
  return null // faellt auf den Viewport zurueck
}

// Der CDN kennt range=360 statt 365 (s. keepa_graph_urls.py im Backend)
const CDN_PERIOD = { 365: 360 }

const chartSrc = (charts, region, variant, period, useCdn) => useCdn
  ? (charts?.fallback?.[region]?.[variant]?.[CDN_PERIOD[period] ?? period] || null)
  : (charts?.[region]?.[variant]?.[period] || null)

function Chart({ charts, regions }) {
  let { theme } = useTheme()
  let [period, setPeriod] = useState(90)
  let [region, setRegion] = useState(() => (Array.isArray(regions) && regions.includes('de') ? 'de' : regions[0]))

  const variant = theme === 'light' ? 'white' : 'dark'
  const variantKey = `${region}|${period}|${variant}`

  // Erst laden, wenn das Chart in Sichtweite kommt -- plus ~5 Zeilen Vorlauf
  // (rootMargin), damit die naechsten beim Scrollen schon da sind. Ohne das
  // Gating wuerde jede Seite sofort ALLE 20-100 Charts anfordern.
  const containerRef = useRef(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    if (visible || !containerRef.current) return
    const observer = new window.IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true)
        observer.disconnect()
      }
    }, { root: findScrollParent(containerRef.current), rootMargin: '700px 0px' })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [visible])

  // src=null: wartet auf Sichtbarkeit/Keepa-Slot; failed: beide Quellen erfolglos.
  // Reset beim Varianten-Wechsel direkt im Render (React-Pattern
  // "adjusting state when a prop changes").
  const [img, setImg] = useState({ key: variantKey, src: null, failed: false })
  if (img.key !== variantKey) {
    setImg({ key: variantKey, src: null, failed: false })
  }

  useEffect(() => {
    if (!visible || img.src || img.failed) return
    let cancelled = false

    const toCdn = () => {
      if (cancelled) return
      const cdnSrc = chartSrc(charts, region, variant, period, true)
      setImg({ key: variantKey, src: cdnSrc, failed: !cdnSrc })
    }

    const keepaSrc = chartSrc(charts, region, variant, period, false)
    if (!keepaSrc || Date.now() < keepaLimited.until) {
      toCdn()
      return
    }

    checkKeepa(keepaSrc).then((ok) => {
      if (cancelled) return
      if (ok) {
        setImg({ key: variantKey, src: keepaSrc, failed: false })
      } else {
        toCdn()
      }
    })

    return () => {
      cancelled = true
    }
  }, [visible, img.src, img.failed, variantKey, charts, region, variant, period])

  // Fehler am <img> selbst: Keepa-Bild -> CDN nachfassen, CDN-Bild -> aufgeben.
  // Key-Guard: ein spaetes onError eines alten Bildes (Variante inzwischen
  // gewechselt) darf den frischen Zustand nicht anfassen.
  const handleError = () => setImg((prev) => {
    if (prev.key !== variantKey) return prev
    const cdnSrc = chartSrc(charts, region, variant, period, true)
    if (prev.src && cdnSrc && prev.src !== cdnSrc) {
      keepaLimited.until = Date.now() + 30000
      return { key: prev.key, src: cdnSrc, failed: false }
    }
    return { ...prev, failed: true }
  })

  return (
    <div
      ref={containerRef}
      style={{
        borderLeft: '1px solid',
        borderImageSource: 'linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(206, 206, 206, 0.7) 48%, rgba(153, 153, 153, 0) 100%)',
        borderImageSlice: 1,
      }}
      className='p-2 grid grid-cols-[auto_auto] gap-1'
    >
      <div className='flex flex-col items-center justify-center gap-2'>
        {[30, 90, 180, 365].map(_period => (
          <button
            key={_period}
            className={classNames(
              'text-xs text-neutral-500 dark:text-neutral-300 px-2 py-0.5 rounded w-fit',
              period === _period && 'bg-neutral-300 dark:bg-neutral-700'
            )}
            onClick={() => setPeriod(_period)}
          >{_period}</button>
        ))}
      </div>

      <div className={styles.chartZoomWrapper}>
        {img.failed ? (
          <div className="w-72 h-28 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center">
            <span className="text-xs text-gray-400 dark:text-gray-500">Kein Chart verfügbar</span>
          </div>
        ) : !img.src ? (
          // Ruhige Flaeche statt Pulse/Spinner (CSS-Animationen x 100 Zeilen
          // kosten Compositing-Leistung und vermitteln Warten)
          <div className="w-72 h-28 bg-gray-100 dark:bg-gray-800 rounded"></div>
        ) : (
          <img
            key={img.src}
            src={img.src}
            alt=""
            referrerPolicy="no-referrer"
            decoding="async"
            onError={handleError}
            className={`h-28 max-w-none w-auto relative bg-gray-100 dark:bg-gray-800 rounded ${styles.chartZoomImg}`}
            style={{ minWidth: '18rem' }}
          />
        )}
      </div>

      <div className='col-start-2 text-left flex flex-row gap-1'>
        {regions.map(_region =>
          <button
            key={_region}
            className={classNames(
              'flex items-center p-1 rounded transition-colors',
              _region === region
                ? theme === 'dark'
                  ? 'bg-primary/30 text-white'
                  : 'bg-neutral-300'
                : theme === 'dark'
                  ? 'hover:bg-neutral-700'
                  : 'hover:bg-neutral-200'
            )}
            onClick={() => setRegion(_region)}
          >
            <Flag country={_region.toUpperCase()} size={16} />
          </button>
        )}
      </div>
    </div>
  )
}

export const MemoChart = memo(Chart)
